import { Component, OnInit, OnChanges, Output, EventEmitter, Input, SimpleChanges } from '@angular/core';
import { FILE_EXTENSION, LAYOUTS } from '../../utils/constants';
import { CarruselService } from '../../services/carrusel.service';
import { decrypt } from '../../services/crypto-js.service';
import Swal from 'sweetalert2';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import * as FileSaver from 'file-saver';
import { getS3File, upload } from '../../utils/s3';
import { environment } from '../../../environments/environment';

export interface Funcion {
  funcionId: number;
  funcion: string;
  url: string;
}

export interface Empleado {
  numeroEmpleado: number;
  nombre: string;
  url:string;
}

export interface AttachmentFile {
  name: string;
  file: File;
  type: string;
  previewUrl: string;
  matched: boolean;
  // tipo 1 — funciones
  funcionId?: number;
  // tipo 3 — empleados por número
  numeroEmpleado?: number;
}

export interface FuncionAttachment {
  funcion: Funcion;
  file?: File;
  previewUrl?: string;
  type?: string;
  url?: string;
  multimediaId?: number | null;
}

export interface EmpleadosAttachment {
  empleado: Empleado
  file?: File;
  previewUrl?: string;
  type?: string;
  url?: string;
  multimediaId?: number | null
}

export interface NEAttachment {
  file?: File;
  previewUrl?: string;
  type?: string;
  url?: string;
  allowedEmployees?: string[];
  multimediaId?: number | null;
}

@Component({
  selector: 'app-attachment-function-component',
  templateUrl: './attachment-functions.component.html',
  styleUrls: ['./attachment-functions.component.scss'],
})
export class AttachmentFunctionsComponente implements OnInit, OnChanges {

  @Output() attachmentsReady = new EventEmitter<any>();
  @Input() existingAttachments: any = null;
  @Input() idBanner: number | null = null;

  funciones: Funcion[] = [];
  empleados: Empleado[] = [];
  bulkFiles: AttachmentFile[] = [];
  selectedFuncion: Funcion | null = null;
  selectedEmpleado: Empleado | null = null;
  funcionAttachments: Map<number, FuncionAttachment> = new Map();
  empleadoAttachments: Map<number, EmpleadosAttachment> = new Map();

  neAttachment: NEAttachment = {
    file: null,
    previewUrl: null,
    type: null,
    url: null,
  };

  user = null;
  typeAttachment = 1;
  employeesArrayList: any = [];
  hasEmployeesFile: boolean = false;
  onUpdateSegmentation: boolean = false;
  editingBanner: boolean;
  showAllowedEmployees: boolean = true;
  fileName: string = null;

  constructor(
    private carruselService: CarruselService,
    private ngxService: NgxUiLoaderService
  ) {}

  ngOnInit(): void {
    // La carga se maneja en ngOnChanges para garantizar que idBanner
    // ya tenga su valor final antes de llamar al servicio
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['idBanner']) {
      const currentValue = changes['idBanner'].currentValue;
      const isFirstChange = changes['idBanner'].firstChange;

      if (isFirstChange && currentValue === null) {
        // Banner nuevo: idBanner es null desde el inicio, arrancar de una vez
        this.loadFunciones();
      } else if (!isFirstChange && currentValue != null) {
        // Si los mapas ya tienen datos el cambio vino de la transición
        // crear→editar tras el primer guardado: no recargar para no perder
        // los archivos que el usuario ya tenía seleccionados.
        const yaHayDatos =
          this.funcionAttachments.size > 0 ||
          this.empleadoAttachments.size > 0 ||
          !!this.neAttachment?.url;

        if (!yaHayDatos) {
          // Banner en edición real: el padre resolvió editingForm() y
          // actualizó el binding con el idBanner del banner a editar
          this.loadFunciones();
        }
      }
    }
  }

  onChangeEmployees(event: any): void {
    this.employeesArrayList = event;
    // Habilitar carga individual de NE solo cuando hay empleados válidos cargados
    this.hasEmployeesFile = !!(event && event.allowed && event.allowed.length > 0);

    if (this.hasEmployeesFile) {
      // Incluir la lista de empleados permitidos en el neAttachment y re-emitir
      this.neAttachment = {
        ...this.neAttachment,
        allowedEmployees: event.allowed.map((e: any) => e.employee_number),
      };
      this.emitAttachments();
    }
  }

  // ─── Carga de funciones desde backend ───────────────────────────────────────

  async loadFunciones(): Promise<void> {
    try {
      this.ngxService.start();

      const responseJobs = await this.carruselService.getNewJobs(this.idBanner ?? undefined)
      const responseNewJob = await decrypt(responseJobs) as any;
      const responseEmps = await this.carruselService.getNewEmployees(this.idBanner ?? undefined)
      const responseNewEmp = await decrypt(responseEmps) as any;


      if (responseNewJob.data.code == 200) {
        this.funciones = responseNewJob.data.items[0].funciones;
        this.funciones.forEach(f => {
          this.funcionAttachments.set(f.funcionId, { funcion: f });
        });

        // Si venimos de edición, poblar el mapa con las URLs existentes del backend        
        if (this.existingAttachments?.funciones?.length > 0) {
          this.existingAttachments.funciones.forEach((item: any) => {
            const funcion = this.funciones.find(f => f.funcionId === item.funcionId);
            if (funcion) {
              this.funcionAttachments.set(funcion.funcionId, {
                funcion,
                url: item.urlMultimedia,
                previewUrl: item.urlMultimedia,
                type: this.getTypeFromUrl(item.urlMultimedia),
                multimediaId: item.multimediaId ?? null,
              });
            }
          });
        }
        
        // Si venimos de edición con segmentación por NE, restaurar neAttachment
        if (this.existingAttachments?.empleados?.urlMultimedia) {
          const emp = this.existingAttachments.empleados;
          this.neAttachment = {
            url: emp.urlMultimedia,
            previewUrl: emp.urlMultimedia,
            type: this.getTypeFromUrl(emp.urlMultimedia),
            allowedEmployees: emp.empleados ?? [],
            multimediaId: emp.multimediaId ?? null,
          };
          // Reconstruir la estructura que espera app-validate-component
          this.employeesArrayList = {
            allowed: (emp.empleados ?? []).map((n: string) => ({ employee_number: n })),
            notAllowed: [],
          };
          this.hasEmployeesFile = true;
        }
        
        if (this.existingAttachments?.funciones?.length > 0 || this.existingAttachments?.empleados) {
          this.emitAttachments();
        }
      }

      if (responseNewEmp?.data?.code == 200) {
        this.empleados = responseNewEmp.data.items[0].empleados ?? responseNewEmp.data.items[0].empleados ?? [];
        this.empleados.forEach(e => {
          this.empleadoAttachments.set(e.numeroEmpleado, { empleado: e });
        });

        // Restaurar adjuntos de tipo 3 si venimos de edición
        if (this.existingAttachments?.multimediaEmpleados?.length > 0) {
          this.existingAttachments.multimediaEmpleados.forEach((item: any) => {
            // El backend devuelve numeroEmpleado como string, el catálogo lo tiene como number
            const emp = this.empleados.find(
              e => e.numeroEmpleado.toString() === item.numeroEmpleado.toString()
            );
            if (emp) {
              this.empleadoAttachments.set(emp.numeroEmpleado, {
                empleado: emp,
                url: item.urlMultimedia,
                previewUrl: item.urlMultimedia,
                type: this.getTypeFromUrl(item.urlMultimedia),
                multimediaId: item.multimediaId ?? null,
              });
            } else {
              // El empleado no está en el catálogo activo pero tiene multimedia guardada:
              // creamos una entrada sintética para que el preview y el emit funcionen
              const syntheticEmp = {
                numeroEmpleado: item.numeroEmpleado,
                nombre: item.nombreEmpleado ?? item.numeroEmpleado.toString(),
                url: '',
              };
              this.empleadoAttachments.set(item.numeroEmpleado, {
                empleado: syntheticEmp,
                url: item.urlMultimedia,
                previewUrl: item.urlMultimedia,
                type: this.getTypeFromUrl(item.urlMultimedia),
                multimediaId: item.multimediaId ?? null,
              });
            }
          });
          this.emitAttachments();
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      this.ngxService.stop();
    }
  }

  // ─── Carga masiva ────────────────────────────────────────────────────────────

  async onBulkFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files = Array.from(input.files);
    this.bulkFiles = [];

    this.ngxService.start();
    try {
      if (this.typeAttachment === 3) {
        // ── Tipo 3: carga masiva por número de empleado ──────────────────────
        await Promise.all(files.map(async (file) => {
          const nameParts = file.name.split(/\.(?=[^\.]+$)/);
          const nameWithoutExt = nameParts[0].toLowerCase().trim();
          const ext = nameParts[1]?.toLowerCase();
          const type = this.validateExtension(ext);
          if (!type) return;

          // El nombre del archivo debe coincidir con numeroEmpleado
          const matchedEmp = this.empleados.find(
            e => e.numeroEmpleado.toString() === nameWithoutExt
          );

          const attachFile: AttachmentFile = {
            name: file.name,
            file,
            type,
            previewUrl: null,
            matched: !!matchedEmp,
            numeroEmpleado: matchedEmp?.numeroEmpleado,
          };
          this.bulkFiles.push(attachFile);

          if (matchedEmp) {
            const timestamp = Date.now();
            const key = `red-unica/banner-carrusel/empleados/empleado_${matchedEmp.numeroEmpleado}_${timestamp}.${ext}`;
            const s3Url = await this.uploadToS3(file, key);
            attachFile.previewUrl = s3Url;

            this.empleadoAttachments.set(matchedEmp.numeroEmpleado, {
              empleado: matchedEmp,
              file,
              type,
              previewUrl: s3Url,
              url: s3Url,
            });
          }
        }));
      } else {
        // ── Tipo 1: carga masiva por función ─────────────────────────────────
        await Promise.all(files.map(async (file) => {
          const nameParts = file.name.split(/\.(?=[^\.]+$)/);
          const nameWithoutExt = nameParts[0].toLowerCase().trim();
          const ext = nameParts[1]?.toLowerCase();
          const type = this.validateExtension(ext);
          if (!type) return;

          // El nombre del archivo debe coincidir con funcionId
          const matchedFun = this.funciones.find(
            f => f.funcionId.toString() === nameWithoutExt
          );

          const attachFile: AttachmentFile = {
            name: file.name,
            file,
            type,
            previewUrl: null,
            matched: !!matchedFun,
            funcionId: matchedFun?.funcionId,
          };
          this.bulkFiles.push(attachFile);

          if (matchedFun) {
            const timestamp = Date.now();
            const key = `red-unica/banner-carrusel/funciones/funcion_${matchedFun.funcionId}_${timestamp}.${ext}`;
            const s3Url = await this.uploadToS3(file, key);
            attachFile.previewUrl = s3Url;

            this.funcionAttachments.set(matchedFun.funcionId, {
              funcion: matchedFun,
              file,
              type,
              previewUrl: s3Url,
              url: s3Url,
            });
          }
        }));
      }
    } catch (err) {
      console.error('Error al subir archivos masivos a S3:', err);
      this.showAlert('Error al subir', 'Hubo un problema al subir uno o más archivos. Intenta de nuevo.');
    } finally {
      this.ngxService.stop();
    }

    input.value = '';
    this.emitAttachments();
  }

  onBulkDragOver(event: DragEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  onBulkDrop(event: DragEvent): void {
    event.preventDefault();
    const fakeEvent = { target: { files: event.dataTransfer?.files } } as any;
    this.onBulkFilesSelected(fakeEvent);
  }

  removeBulkFile(index: number): void {
    const removed = this.bulkFiles[index];

    if (this.typeAttachment === 3 && removed.numeroEmpleado != null) {
      const entry = this.empleadoAttachments.get(removed.numeroEmpleado);
      if (entry && entry.file === removed.file) {
        this.empleadoAttachments.set(removed.numeroEmpleado, { empleado: entry.empleado });
      }
    } else if (removed.funcionId != null) {
      const entry = this.funcionAttachments.get(removed.funcionId);
      if (entry && entry.file === removed.file) {
        this.funcionAttachments.set(removed.funcionId, { funcion: entry.funcion });
      }
    }

    this.bulkFiles.splice(index, 1);
    this.emitAttachments();
  }

  // ─── Panel de funciones / empleados ─────────────────────────────────────────

  selectFuncion(funcion: Funcion): void {
    this.selectedFuncion = funcion;
    this.selectedEmpleado = null;
  }

  selectEmpleado(empleado: Empleado): void {
    this.selectedEmpleado = empleado;
    this.selectedFuncion = null;
  }

  /** Devuelve el attachment de una función (tipo 1). */
  getFuncionAttachment(funcionId: number): FuncionAttachment | undefined {
    return this.funcionAttachments.get(funcionId);
  }

  /** Devuelve el attachment de un empleado (tipo 3). */
  getEmpleadoAttachment(numeroEmpleado: number): EmpleadosAttachment | undefined {
    return this.empleadoAttachments.get(numeroEmpleado);
  }

  getNEAttachment(): NEAttachment {
    return this.neAttachment;
  }

  /** Verifica si una función tiene archivo asignado. */
  hasAttachment(funcionId: number): boolean {
    const entry = this.funcionAttachments.get(funcionId);
    return !!(entry?.file || entry?.url);
  }

  /** Verifica si un empleado tiene archivo asignado. */
  hasEmpleadoAttachment(numeroEmpleado: number): boolean {
    const entry = this.empleadoAttachments.get(numeroEmpleado);
    return !!(entry?.file || entry?.url);
  }

  hasAttachmentNe(): boolean {
    return !!(this.neAttachment?.file || this.neAttachment?.url);
  }

  // ─── Selector individual por función / empleado ──────────────────────────────

  async onIndividualFileSelected(event: Event): Promise<void> {
    const isType1 = this.typeAttachment === 1;
    const isType3 = this.typeAttachment === 3;

    if (isType1 && !this.selectedFuncion) return;
    if (isType3 && !this.selectedEmpleado) return;

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const ext = file.name.split(/\.(?=[^\.]+$)/)[1]?.toLowerCase();
    const type = this.validateExtension(ext);

    if (!type) {
      this.showAlert('Formato no permitido', 'Solo se aceptan archivos PNG, JPG, MP4 y PDF.');
      input.value = '';
      return;
    }

    this.ngxService.start();
    try {
      if (isType1) {
        const timestamp = Date.now();
        const key = `red-unica/banner-carrusel/funciones/funcion_${this.selectedFuncion.funcionId}_${timestamp}.${ext}`;
        const s3Url = await this.uploadToS3(file, key);
        const existing = this.funcionAttachments.get(this.selectedFuncion.funcionId);
        this.funcionAttachments.set(this.selectedFuncion.funcionId, {
          ...existing,
          file,
          type,
          previewUrl: s3Url,
          url: s3Url,
        });
        input.value = '';
        this.emitAttachments();
      }

      if (isType3) {
        const timestamp = Date.now();
        const key = `red-unica/banner-carrusel/empleados/empleado_${this.selectedEmpleado.numeroEmpleado}_${timestamp}.${ext}`;
        const s3Url = await this.uploadToS3(file, key);
        const existing = this.empleadoAttachments.get(this.selectedEmpleado.numeroEmpleado);
        this.empleadoAttachments.set(this.selectedEmpleado.numeroEmpleado, {
          ...existing,
          empleado: this.selectedEmpleado,
          file,
          type,
          previewUrl: s3Url,
          url: s3Url,
        });
        input.value = '';
        this.emitAttachments();
      }

      if (this.typeAttachment === 2) {
        const timestamp = Date.now();
        const key = `red-unica/banner-carrusel/NE/ne_${timestamp}.${ext}`;
        const s3Url = await this.uploadToS3(file, key);
        this.neAttachment = {
          ...this.neAttachment,
          file,
          type,
          previewUrl: s3Url,
          url: s3Url,
        };
        input.value = '';
        this.emitAttachments();
      }
    } catch (err) {
      console.error('Error al subir archivo a S3:', err);
      this.showAlert('Error al subir', 'Hubo un problema al subir el archivo. Intenta de nuevo.');
      input.value = '';
    } finally {
      this.ngxService.stop();
    }
  }

  removeIndividualAttachment(): void {
    if (this.typeAttachment === 2) {
      this.neAttachment = { file: null, previewUrl: null, type: null, url: null };
      this.emitAttachments();
      return;
    }

    if (this.typeAttachment === 3 && this.selectedEmpleado) {
      this.empleadoAttachments.set(this.selectedEmpleado.numeroEmpleado, { empleado: this.selectedEmpleado });
      this.bulkFiles = this.bulkFiles.filter(f => f.numeroEmpleado !== this.selectedEmpleado.numeroEmpleado);
      this.emitAttachments();
      return;
    }

    if (this.typeAttachment === 1 && this.selectedFuncion) {
      this.funcionAttachments.set(this.selectedFuncion.funcionId, { funcion: this.selectedFuncion });
      this.bulkFiles = this.bulkFiles.filter(f => f.funcionId !== this.selectedFuncion.funcionId);
      this.emitAttachments();
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  /**
   * Sube un archivo al bucket structureBucket de S3 y retorna la URL pública.
   */
  async uploadToS3(file: File, key: string): Promise<string> {
    const result: any = await upload(
      environment.AWS_CONFIG.structureBucket,
      key,
      file,
      file.type
    );
    return result.Location as string;
  }

  /** Infiere el tipo MIME a partir de la extensión de una URL. */
  getTypeFromUrl(url: string): string {
    if (!url) return 'image/*';
    const ext = url.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png'].includes(ext)) return 'image/*';
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'mp4') return 'video/mp4';
    return 'image/*';
  }

  validateExtension(extension: string): string | false {
    if (!extension) return false;
    extension = extension.toLowerCase();
    if ([FILE_EXTENSION.JPEG, FILE_EXTENSION.JPG, FILE_EXTENSION.PNG].includes(extension)) {
      return 'image/*';
    }
    if (extension === FILE_EXTENSION.PDF) return 'application/pdf';
    if (extension === FILE_EXTENSION.MP4) return 'video/mp4';
    return false;
  }

  isImage(type: string): boolean {
    return type === 'image/*';
  }

  isVideo(type: string): boolean {
    return type === 'video/mp4';
  }

  isPdf(type: string): boolean {
    return type === 'application/pdf';
  }

  showAlert(title: string, message: string): void {
    Swal.fire({
      title,
      icon: 'info',
      text: message,
      confirmButtonColor: '#545D6E',
      confirmButtonText: '<span style="color: #ffffff"> Entendido </span>',
    });
  }

  emitAttachments(): void {
    const funciones = Array.from(this.funcionAttachments.values())
      .filter(a => a.url)
      .map(a => ({
        funcionId: a.funcion.funcionId,
        funcion: a.funcion.funcion,
        urlMultimedia: a.url,
        multimediaId: a.multimediaId ?? null,
      }));

    const multimediaEmpleados = Array.from(this.empleadoAttachments.values())
      .filter(a => a.url)
      .map(a => ({
        numeroEmpleado: a.empleado.numeroEmpleado,
        nombreEmpleado: a.empleado.nombre,
        urlMultimedia: a.url,
        multimediaId: a.multimediaId ?? null,
      }));

    const neBase = this.neAttachment;
    const empleados = neBase?.url
      ? {
          empleados: neBase.allowedEmployees ?? [],
          urlMultimedia: neBase.url,
          multimediaId: neBase.multimediaId ?? null,
        }
      : null;

    const payload = { typeAttachment: this.typeAttachment, funciones, multimediaEmpleados, empleados };
    this.attachmentsReady.emit(payload);
  }

  onChangeTypeAttachment(type: number): void {
    this.typeAttachment = type;
    this.bulkFiles = [];
    this.selectedFuncion = null;
    this.selectedEmpleado = null;
  }

  async downloadFile(templateName: string): Promise<void> {
    this.ngxService.start();
    const layout: string = templateName === 'Carga_empleados' ? LAYOUTS.EMPLOYEES : LAYOUTS.CUSTOMIZED;
    try {
      const file = await getS3File(environment.AWS_CONFIG.credentials.structureBucket, layout);
      this.saveAsExcelFile(file.Body, templateName);
    } catch (err) {
      this.ngxService.stop();
      this.showAlert('Error en descarga', 'Lo sentimos, hubo un problema en la descarga del archivo, favor de volver a intentar.');
    }
  }

  saveAsExcelFile(buffer: any, name: string): void {
    try {
      const data: Blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });
      FileSaver.saveAs(data, name + '.xlsm');
      this.ngxService.stop();
    } catch (e) {
      console.error(e);
    }
  }
}
